import './MatchCategories.less';

import React, { FC, useEffect } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';
import { loggedIn$ } from '../../subs';
import {
    detailMarketCategory$$,
    detailMarketCategoryIds$,
    marketsCount$,
    combinableMarketsCount$,
    selectedMarketCategoryId$,
    hasCombinableMarkets$,
    hasCombinableBets$,
    detailMarketIds$$,
    detailMarket$$
} from '../../push/detail/subs';
import clsx from 'clsx';
import { localizeMap } from '../../utils';
import { CashinToggle } from '../Filter';
import { matchDetailSelectCategory } from '../../push/detail/handlers';
import Tabs, { useTabs } from '../Tabs';
import i18n from '../../../../common/i18n';
import { isProfileCombinableBetsEnabled } from '../../../../profiles';
import { isMobileChannel } from '../../../../common/util';
import { computeCombinableBets } from '../../../combinable-bets/handlers';

const ALL_CATEGORIES_ID = 'ALL';
export const COMBINABLE_BETS = 'COMBINABLE_BETS';

const MarketCategories: FC<{ matchId?; otherClass?; sectionType: string; dark?: boolean }> = ({
    matchId,
    otherClass,
    sectionType,
    dark
}) => {
    const derefSub = useReframe();
    const marketCategoryIds = derefSub(detailMarketCategoryIds$);
    const hasCombinableMarkets =
        sectionType === 'LIVE' ? derefSub(hasCombinableMarkets$) : derefSub(hasCombinableBets$);

    return !marketCategoryIds?.size && !hasCombinableMarkets ? null : (
        <MarketCategoriesView
            marketCategoryIds={marketCategoryIds}
            matchId={matchId}
            hasCombinableMarkets={hasCombinableMarkets}
            otherClass={otherClass}
            dark={dark}
        />
    );
};

const isCombinableTabVisible = (marketsCountCombinable, combinableTabFirstMarket) =>
    marketsCountCombinable > 1 ||
    (marketsCountCombinable === 1 &&
        (combinableTabFirstMarket?.ticketCombinable || combinableTabFirstMarket?.marketCombinable));

const MarketCategoriesView: FC<{
    marketCategoryIds: any;
    matchId?;
    hasCombinableMarkets: boolean;
    otherClass?: string;
    dark?: boolean;
}> = ({ marketCategoryIds, matchId, hasCombinableMarkets, otherClass = 'bg-black-3', dark }) => {
    const { derefSub, subscribe } = useReframe();
    const loggedIn = derefSub(loggedIn$);
    const selected = derefSub(selectedMarketCategoryId$);
    const marketsCountAll = derefSub(marketsCount$);
    const marketsCountCombinable = derefSub(combinableMarketsCount$);
    const combinableTabMarketsIds = subscribe(detailMarketIds$$(COMBINABLE_BETS)) || [];
    const combinableTabFirstMarket =
        combinableTabMarketsIds.length && subscribe(detailMarket$$(combinableTabMarketsIds[0]));
    const showCombinableTab = isCombinableTabVisible(marketsCountCombinable, combinableTabFirstMarket);

    const [onTabClick, setArrowVisible, wrapRef, scrollElementRef, active, leftArrow, rightArrow, repaint] = useTabs(
        ALL_CATEGORIES_ID
    );
    useEffect(() => {
        if ((marketCategoryIds?.length && !active) || (marketCategoryIds && !marketCategoryIds.includes(active))) {
            onTabClick(null, marketCategoryIds[marketCategoryIds.length - 1]);
        }
    }, [marketCategoryIds, active, onTabClick]);

    // if only 1 combinable market with no ticketCombinable, switch to all bets
    useEffect(() => {
        if (!showCombinableTab && selected === COMBINABLE_BETS) dispatch(matchDetailSelectCategory(null));
    }, [selected, showCombinableTab]);

    useEffect(() => {
        dispatch(computeCombinableBets());
    }, [marketsCountAll]);

    const getBtnClass = (active: boolean) =>
        clsx(' market-category my-15 mr-10', {
            'c-orange active-category ': active,
            'c-black-50 inactive-category': !active
        });
    const combinableGroup = isProfileCombinableBetsEnabled() && hasCombinableMarkets;
    const showAllBtn = !marketCategoryIds.isEmpty() || combinableGroup;
    const mobileChannel = isMobileChannel();

    return (
        <Tabs
            wrapRef={wrapRef}
            otherClass={`${otherClass} c-default fs-14 no-wrap ${mobileChannel ? 'px-10' : 'px-20'}`}
            defaultColor={false}
            setArrowVisible={setArrowVisible}
            leftArrowVisible={leftArrow}
            rightArrowVisible={rightArrow}
            repaint={repaint}
            transparent
            dark={dark}
        >
            {loggedIn && mobileChannel && marketCategoryIds?.size > 0 && (
                <div className="flex-center mr-15">
                    <CashinToggle mini />
                </div>
            )}
            {showAllBtn && (
                <button
                    ref={active === ALL_CATEGORIES_ID ? scrollElementRef : null}
                    className={getBtnClass(selected == null)}
                    onClick={() => {
                        onTabClick(null, ALL_CATEGORIES_ID);
                        dispatch(matchDetailSelectCategory(null));
                    }}
                    data-atid="market-category"
                >
                    {i18n('nui.turbolive.markets.all') + ` (${marketsCountAll})`}
                </button>
            )}
            {combinableGroup && showCombinableTab && (
                <button
                    ref={active === COMBINABLE_BETS ? scrollElementRef : null}
                    className={getBtnClass(selected === COMBINABLE_BETS)}
                    onClick={() => {
                        onTabClick(null, COMBINABLE_BETS);
                        dispatch(matchDetailSelectCategory(COMBINABLE_BETS));
                    }}
                    data-atid="market-category"
                    data-badge="nové"
                >
                    {i18n('nui.turbolive.combinable.bets') + ` (${marketsCountCombinable})`}
                </button>
            )}
            {marketCategoryIds?.map(id => (
                <MarketCategory
                    key={id}
                    marketCategoryId={id}
                    matchId={matchId}
                    btnClass={getBtnClass(id === selected)}
                    scrollElementRef={scrollElementRef}
                    setTab={onTabClick}
                    active={id === active}
                />
            ))}
        </Tabs>
    );
};

const MarketCategory: FC<{
    marketCategoryId: string;
    matchId: string;
    btnClass?: string;
    scrollElementRef;
    setTab: any;
    active?: boolean;
}> = ({ marketCategoryId, btnClass, scrollElementRef, setTab, active }) => {
    const derefSub = useReframe();
    const marketCategory = derefSub(detailMarketCategory$$(marketCategoryId));

    if (!marketCategory) {
        return null;
    }

    return (
        <button
            ref={active ? scrollElementRef : null}
            className={btnClass}
            onClick={() => {
                setTab(null, marketCategoryId);
                dispatch(matchDetailSelectCategory(marketCategoryId));
            }}
            data-atid="market-category"
        >
            {localizeMap(marketCategory.get('name')) + ` (${marketCategory.get('marketIds')?.size || 0})`}
        </button>
    );
};

export default MarketCategories;
